/**
 * Standard pricing (INR, excl. GST). Positioned above template shops and at
 * the upper end of mid-market agencies — custom design, React/Next.js,
 * senior team. Market bands come from published prices of Pune / Mumbai /
 * Nashik providers and cost guides, researched 2026-09-23 (see SEO-PLAN.md).
 * Edit numbers here only; the pricing page, city pages and FAQs read from it.
 */
export const PRICING_UPDATED = '2026';

export const PLANS = [
  {
    slug: 'landing-page',
    name: 'Landing page',
    bestFor: 'Campaigns & launches',
    from: '₹35,000',
    range: '₹35,000 – ₹75,000',
    timeline: '2–3 weeks',
    includes: ['1 long-form custom-designed page', 'Copy structure & conversion-focused layout', 'Lead form + WhatsApp CTA', 'Analytics & ad pixel setup', 'Mobile-first, sub-2s load'],
    link: '/services/website-development/',
  },
  {
    slug: 'business-website',
    name: 'Business website',
    bestFor: 'SMEs, startups, manufacturers',
    from: '₹90,000',
    range: '₹90,000 – ₹2,50,000',
    timeline: '6–10 weeks',
    includes: ['5–15 custom-designed pages (no templates)', 'Easy CMS for pages, blog & news', 'On-page SEO, schema & sitemap', 'Enquiry, WhatsApp & Google Business integration', 'Marathi / Hindi pages on request', '1 month post-launch support'],
    link: '/services/website-development/',
    featured: true,
  },
  {
    slug: 'ecommerce',
    name: 'Ecommerce store',
    bestFor: 'D2C brands & retailers',
    from: '₹1,50,000',
    range: '₹1,50,000 – ₹4,00,000 (Shopify) · custom from ₹4,00,000',
    timeline: '10–16 weeks',
    includes: ['Shopify, Hydrogen or custom headless build', 'UPI, cards, COD & GST invoices', 'Catalogue, filters & fast product pages', 'Shipping, WhatsApp & email automations', 'Product & order import'],
    link: '/services/shopify-development/',
  },
  {
    slug: 'web-app',
    name: 'Web app / custom software',
    bestFor: 'Portals, dashboards, SaaS',
    from: '₹3,00,000',
    range: '₹3,00,000 – ₹12,00,000',
    timeline: '8–14 weeks',
    includes: ['Discovery sprint & written spec', 'Login, roles & admin panel', 'APIs & integrations (ERP, CRM, payments)', 'Automated tests & CI', 'Hosting setup & hand-over docs'],
    link: '/services/web-app-development/',
  },
  {
    slug: 'mobile-app',
    name: 'Mobile app',
    bestFor: 'Android + iOS',
    from: '₹4,00,000',
    range: '₹4,00,000 – ₹15,00,000',
    timeline: '10–16 weeks',
    includes: ['One codebase for Android & iOS (Flutter / React Native)', 'Backend, admin panel & push notifications', 'Payments & offline support', 'Play Store & App Store submission', 'Crash reporting & analytics'],
    link: '/services/mobile-app-development/',
  },
  {
    slug: 'ai',
    name: 'AI chatbot & automation',
    bestFor: 'Support, search, ops',
    from: '₹2,00,000',
    range: '₹2,00,000 – ₹8,00,000',
    timeline: '6–12 weeks',
    includes: ['AI assistant or document search (RAG) on your data', 'WhatsApp / website / internal tool integration', 'Evaluation set & guardrails', 'Usage cost limits & monitoring', 'Admin to update knowledge'],
    link: '/services/ai-solutions/',
  },
];

export const RETAINERS = [
  { name: 'Care', price: '₹7,500 / month', body: 'Hosting checks, backups, security updates, uptime monitoring and up to 2 hours of edits a month.' },
  { name: 'Growth', price: '₹25,000 / month', body: 'Everything in Care plus SEO health, Core Web Vitals, new pages and landing pages — up to 8 hours a month.' },
  { name: 'Product partner', price: 'From ₹75,000 / month', body: 'Dedicated design and engineering days for apps and stores that ship new features every month.' },
];

export const MARKET = {
  intro:
    'We looked at published prices from template website shops, agencies, IndiaMART and Justdial listings, and cost guides from Pune, Mumbai and Nashik providers. Prices vary 10–50× for the same “website” — mostly because of templates vs custom design, and who builds it.',
  rows: [
    { project: 'Basic business website', budget: '₹3,500 – ₹15,000', mid: '₹20,000 – ₹90,000', ours: 'from ₹90,000' },
    { project: 'Custom corporate website', budget: '—', mid: '₹90,000 – ₹2,00,000', ours: '₹90,000 – ₹2,50,000' },
    { project: 'Ecommerce (Shopify)', budget: '₹10,000 – ₹60,000', mid: '₹60,000 – ₹1,50,000', ours: '₹1,50,000 – ₹4,00,000' },
    { project: 'Custom ecommerce', budget: '—', mid: '₹1,50,000 – ₹4,00,000', ours: 'from ₹4,00,000' },
    { project: 'Custom web application', budget: '—', mid: '₹1,00,000 – ₹5,00,000', ours: '₹3,00,000 – ₹12,00,000' },
    { project: 'Mobile app (Android + iOS)', budget: '₹25,000 – ₹2,50,000', mid: '₹2,00,000 – ₹15,00,000', ours: '₹4,00,000 – ₹15,00,000' },
    { project: 'Maintenance', budget: '₹1,500 – ₹3,000 / mo', mid: '₹3,000 – ₹15,000 / mo', ours: 'from ₹7,500 / mo' },
  ],
  note: 'Market ranges from publicly listed prices and cost guides of providers in Pune, Mumbai and Nashik, September 2026. Budget prices usually mean a pre-made template, shared hosting bundle and limited revisions. All NioDelta prices exclude 18% GST.',
};

export const COST_FACTORS = [
  { k: 'Template vs custom design', v: 'A ₹5,000 website reuses a theme thousands of others use. Custom design is built around your brand, content and buyers — it is the biggest single price difference in the market.' },
  { k: 'Number of pages & content', v: 'More pages means more design, copy and SEO work. We can write and structure copy for you, or work with your existing content.' },
  { k: 'Features & integrations', v: 'Payments, logins, booking, ERP / CRM sync, multi-language and dashboards each add build and testing time.' },
  { k: 'Platform', v: 'Shopify and CMS builds are faster to launch; custom React / Next.js builds cost more but load faster, scale further and avoid plugin bloat.' },
  { k: 'Speed & SEO requirements', v: 'Performance budgets, schema markup and migration redirects protect rankings. We include them by default rather than as add-ons.' },
  { k: 'Ongoing support', v: 'Hosting, security updates and small changes after launch. Budget quotes often leave this out and charge renewals later.' },
];

export const PRICING_FAQ = [
  { q: 'How much does website development cost in Pune?', a: 'In Pune, template websites start around ₹4,000–₹15,000, typical agency websites cost ₹20,000–₹90,000, and custom-designed business websites cost ₹90,000–₹2,50,000. With NioDelta a custom business website starts at ₹90,000 (excl. GST).' },
  { q: 'What is the website design cost in Mumbai?', a: 'Mumbai agencies publish ₹20,000–₹90,000 for professional business websites and ₹90,000–₹2,00,000+ for custom corporate sites. Our custom business websites start at ₹90,000 and ecommerce stores at ₹1,50,000.' },
  { q: 'How much does a website cost in Nashik?', a: 'Nashik listings start around ₹6,000–₹30,000 for basic sites. A custom-designed, SEO-ready business website with NioDelta starts at ₹90,000; export catalogues and dealer portals are quoted by scope.' },
  { q: 'Why are some websites only ₹5,000?', a: 'Very low prices usually mean a pre-made template, limited pages and revisions, shared hosting bundles and renewal fees. That can suit a personal page; for a business that needs leads, speed and search rankings, custom work pays for itself.' },
  { q: 'Do your prices include GST, domain and hosting?', a: 'Prices exclude 18% GST. Domain and hosting are billed at cost in your name, so you always own them — typically ₹3,000–₹15,000 a year for a business website.' },
  { q: 'How do payments work?', a: 'Fixed quote, usually 40% to start, 30% at design sign-off and 30% at launch. Larger apps are split into monthly milestones.' },
  { q: 'How much does mobile app development cost?', a: 'A cross-platform Android + iOS app with backend and admin panel starts at ₹4,00,000 with us; most business apps land between ₹4 and ₹15 lakh depending on features.' },
];
